import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button, Navbar } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCheckCircle, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import logo from "assets/logo.png";
import { changePassword } from "../../api/auth";
import ConfirmationModal from "../general/ConfirmationModal";

interface ChangePasswordProps {
  email: string;
  verificationCode: string;
}

const ChangePassword: React.FC<ChangePasswordProps> = ({
  email,
  verificationCode,
}) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordsMatch, setPasswordsMatch] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const navigate = useNavigate();
  /**
   * Effect to reset passwordsMatch after 3 seconds
   */
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!passwordsMatch) {
      timer = setTimeout(() => {
        setPasswordsMatch(true);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [passwordsMatch]);

  /**
   * Handles changes in the password input
   * @param e - The input change event
   */
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setIsPasswordValid(newPassword.length >= 8);
    setPasswordsMatch(true);
  };

  /**
   * Handles changes in the confirm password input
   * @param e - The input change event
   */
  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setConfirmPassword(e.target.value);
    setPasswordsMatch(true);
  };

  /**
   * Handles form submission for password change
   * @param e - The form submit event
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password === confirmPassword && isPasswordValid) {
      setIsLoading(true);
      try {
        await changePassword(password, email, verificationCode);
        setIsSuccess(true);
        setShowModal(true);
      } catch (error) {
        setIsSuccess(false);
        setShowModal(true);
      } finally {
        setIsLoading(false);
      }
    } else {
      setPasswordsMatch(password === confirmPassword);
      setIsPasswordValid(password.length >= 8);
      setPassword("");
      setConfirmPassword("");
    }
  };

  /**
   * Handles navigation back
   */
  const handleBack = () => {
    navigate(-1);
  };

  /**
   * Handles modal close and navigation
   */
  const handleModalClose = () => {
    setShowModal(false);
    if (isSuccess) {
      // Navigate to login page after successful password change
      setTimeout(() => {
        navigate("/login");
      }, 500);
    }
  };

  return (
    <>
      <Navbar style={{ backgroundColor: "#FBE584" }}>
        <Navbar.Brand href="#">
          <img src={logo} alt="Logo de la Boutique" style={{ width: "50px" }} />
        </Navbar.Brand>
      </Navbar>
      <Button variant="secondary" onClick={handleBack} className="back-button">
        <FontAwesomeIcon icon={faArrowLeft} />
      </Button>
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col xs={12} md={6} className="text-center">
            <img
              src={logo}
              alt="Logo"
              className="mb-4"
              style={{ width: "150px" }}
            />
            <h1 className="fw-bold mb-4">Cambio de contraseña</h1>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="text-center w-100">
                  Ingresa la nueva contraseña
                </Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Contraseña"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  className="text-center w-75 mx-auto"
                  isInvalid={!passwordsMatch || !isPasswordValid}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="text-center w-100">
                  Confirma la contraseña
                </Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirmar contraseña"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  required
                  className="text-center w-75 mx-auto"
                  isInvalid={!passwordsMatch || !isPasswordValid}
                />
                {!passwordsMatch && (
                  <Form.Control.Feedback type="invalid" className="text-center">
                    Las contraseñas no coinciden
                  </Form.Control.Feedback>
                )}
                {!isPasswordValid && (
                  <Form.Control.Feedback type="invalid" className="text-center">
                    La contraseña debe tener al menos 8 caracteres
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Button 
                variant="primary" 
                type="submit" 
                className="w-50 mb-3"
                disabled={isLoading}
              >
                {isLoading ? "Cambiando..." : "Aceptar"}
              </Button>
            </Form>
          </Col>
        </Row>
      </Container>
      
      <ConfirmationModal
        show={showModal}
        onHide={handleModalClose}
        title={isSuccess ? "Éxito" : "Error"}
        message={
          isSuccess
            ? "Tu contraseña ha sido cambiada exitosamente. Serás redirigido al login."
            : "Hubo un error al cambiar tu contraseña. Por favor, inténtalo nuevamente."
        }
        icon={isSuccess ? faCheckCircle : faXmarkCircle}
        iconColor={isSuccess ? "text-success" : "text-danger"}
      />
    </>
  );
};

export default ChangePassword;
